import { memo, useState } from 'react';

import { useRecoilValue } from 'recoil';
import { useTranslation } from 'react-i18next';

import {
  CollapseCard,
  GridItem,
  Icons,
  PendingContent,
  Spinner,
  Switch,
  Tooltip,
  notify,
} from 'plume-ui';
import { CollapseCardStyles } from 'plume-ui/dist/components/Card/CollapseCard';

import FormattedMessage from '../../../../utils/components/FormattedMessage';
import { AdjustableComponent } from '../../../../utils/hooks/useClasses';
import { DependencyContainer } from '../../../../DependencyContainer';
import { useFetchData } from '../../../../hooks/useFetchData';
import { TroubleshootingWorkflow } from './types';
import { activePartnerAtom, userAtom } from '../../../../state';
import SignalIcon from '../../../../assets/icons';
import { Role } from '../../../../types';

type IssueResolutionProps = {
  editAllowed: boolean;
};

type IssueResolutionStyles = {};

const { configurationService } = new DependencyContainer();

const IssueResolution: AdjustableComponent<
  IssueResolutionProps,
  IssueResolutionStyles
> = ({ editAllowed }) => {
  const { t } = useTranslation();

  const activePartner = useRecoilValue(activePartnerAtom);
  const user = useRecoilValue(userAtom);

  const [workflows, setWorkflows] = useState<TroubleshootingWorkflow[]>([]);
  const [loadingUpdateWorkflow, setLoadingUpdateWorkflow] = useState<string[]>(
    [],
  );

  const [loadingWorkflows] = useFetchData<TroubleshootingWorkflow[]>(
    async () => {
      if (!activePartner?.id) return;
      const listItems = await configurationService.getTroubleshootingWorkflows(
        activePartner.id,
      );
      setWorkflows(listItems);
      return listItems;
    },
    undefined,
    [activePartner],
    () => activePartner === undefined,
  );

  const onToggleWorkflow = async (workflow: TroubleshootingWorkflow) => {
    try {
      if (!activePartner || !activePartner.id) return;
      setLoadingUpdateWorkflow((prev) => [...prev, workflow.id]);
      const newStatus = !workflow.enabled;
      const resp = await configurationService.updateTroubleshootingWorkflowStatus(
        activePartner.id,
        workflow.id,
        newStatus,
      );
      setLoadingUpdateWorkflow((prev) =>
        prev.filter((id) => id !== workflow.id),
      );

      if (resp.status >= 200 && resp.status < 300) {
        const newWorkflows = [...workflows];
        const index = newWorkflows.findIndex((w) => w.id === workflow.id);
        newWorkflows[index].enabled = newStatus;
        setWorkflows(newWorkflows);
        notify({
          type: 'success',
          title: t('success'),
          body: newStatus
            ? t('configuration.issueResolution.successEnabled')
            : t('configuration.issueResolution.successDisabled'),
        });
      } else {
        notify({
          type: 'error',
          title: t('error'),
          body: t('genericErrorMessage'),
        });
      }
    } catch (error) {
      notify({
        type: 'error',
        title: t('error'),
        body: t('genericErrorMessage'),
      });
      setLoadingUpdateWorkflow((prev) =>
        prev.filter((id) => id !== workflow.id),
      );
    }
  };

  const toggleDisabled =
    !editAllowed ||
    (user?.role !== Role.PlumeAdmin && user?.role !== Role.PartnerAdmin);

  if (!loadingWorkflows && workflows && !workflows.length) return null;

  return (
    <GridItem colSpan="12" tabletColSpan="12">
      <PendingContent loading={loadingWorkflows} loader={Spinner}>
        <CollapseCard
          title={
            <div className="Configuration__collapseCard__title">
              <FormattedMessage id="configuration.issueResolution.title" />
            </div>
          }
          subtitle={t('configuration.issueResolution.subtitle')}
          classes={(current: CollapseCardStyles) => ({
            ...current,
            root: `${current.root} Configuration__collapseCard`,
            content: `${current.content} Configuration__collapseCard__content `,
            innerContent: `${current.innerContent} Configuration__collapseCard__issueResolutionContent`,
          })}
        >
          {workflows &&
            workflows.map((workflow) => {
              const tooltipContent = t(
                `configuration.issueResolution.tooltips.${workflow.id}`,
                {
                  defaultValue: '',
                },
              );
              return (
                <div
                  key={workflow.id}
                  className="Configuration__collapseCard__networkIssue"
                >
                  <SignalIcon
                    name={workflow.id}
                    width={64}
                    height={64}
                    className="Configuration__collapseCard__networkIssue__icon"
                  />
                  <span>{workflow.workflowName}</span>
                  {tooltipContent && (
                    <Tooltip
                      label={tooltipContent}
                      position="below"
                      alignment="left"
                      overrideMaxCharacters
                      openInPortal
                    >
                      <Icons.InfoIcon width={16} />
                    </Tooltip>
                  )}

                  <Switch
                    title={''}
                    disabled={
                      toggleDisabled ||
                      loadingUpdateWorkflow.includes(workflow.id)
                    }
                    selected={workflow.enabled}
                    onToggle={() => onToggleWorkflow(workflow)}
                    data-testid={`${workflow.id}-switch`}
                  />
                </div>
              );
            })}
        </CollapseCard>
      </PendingContent>
    </GridItem>
  );
};

export default memo(IssueResolution);
